import { getSignatureStatus, type SignatureSaleData } from '../AdditionalSales/bones/signatureStatus'

export type TimelineStepKey = 'created' | 'synced' | 'signed'
export type TimelineStepState = 'done' | 'current' | 'error' | 'todo'
export type SaleStateKey =
  | 'idle'
  | 'creating'
  | 'create_error'
  | 'created'
  | 'sync_error'
  | 'synced'
  | 'awaiting_signature'
  | 'signature_error'
  | 'signed'

export interface TimelineStep {
  key: TimelineStepKey
  state: TimelineStepState
}

export interface SaleTimeline {
  stateKey: SaleStateKey
  steps: TimelineStep[]
}

export interface TimelineSaleData extends SignatureSaleData {
  actionsInProgress?: boolean
  createdInDatabase?: boolean
  saleDatabaseId?: number | null
  saleCreateError?: string | null
  zohoContractDetailId?: string | null
  syncPartnerError?: string | null
  errorPartnerSync?: string | null
}

const stepState = (done: boolean, failed: boolean, reached: boolean): TimelineStepState => {
  if (done) return 'done'
  if (failed) return 'error'

  return reached ? 'current' : 'todo'
}

export const deriveSaleTimeline = (data: TimelineSaleData = {}): SaleTimeline => {
  const isCreating = Boolean(data.actionsInProgress)
  const isCreated = Boolean(data.createdInDatabase || data.saleDatabaseId)
  const isCreateFailed = !isCreated && Boolean(data.saleCreateError)
  const isSynced = Boolean(data.zohoContractDetailId)
  const isSyncFailed = isCreated && !isSynced && Boolean(data.syncPartnerError || data.errorPartnerSync)
  const { isSigned, isCodeFailed, showSignatureProcess } = getSignatureStatus(data)

  const steps: TimelineStep[] = [
    { key: 'created', state: stepState(isCreated, isCreateFailed, isCreating) },
    { key: 'synced', state: stepState(isSynced, isSyncFailed, isCreated) },
  ]
  if (showSignatureProcess) {
    steps.push({ key: 'signed', state: stepState(isSigned, isCodeFailed, isSynced) })
  }

  const stateChecks: Array<[boolean, SaleStateKey]> = [
    [isSigned, 'signed'],
    [showSignatureProcess && isCodeFailed, 'signature_error'],
    [showSignatureProcess, 'awaiting_signature'],
    [isSynced, 'synced'],
    [isSyncFailed, 'sync_error'],
    [isCreated, 'created'],
    [isCreateFailed, 'create_error'],
    [isCreating, 'creating'],
  ]
  const stateKey = stateChecks.find(([matches]) => matches)?.[1] ?? 'idle'

  return { stateKey, steps }
}
